/**
 * Atelier Hollis — the Gold-only garment storefront, the shop's second face
 * (design/quick-specs/tailor-shop.md). The shop's header switch swaps between
 * the Black Market and this; it is a view swap, not another category tab.
 *
 * ─── Layout ─────────────────────────────────────────────────────────────────
 * Landscape (844x390 first): the Fitting Stage on the left, the rail on the
 * right with the Golden Set banner above it. Below 720px the stage becomes a
 * bottom sheet the player pulls up, and the cards go two to a row.
 *
 * ─── The stage is persistent ────────────────────────────────────────────────
 * The shop repaints its body on every store push. The stage holds the 3D
 * canvas, which must not be torn out on each repaint, so this view builds its
 * root ONCE and only rewrites the rail column and the stage's controls. The
 * preview is the paperdoll's (`wardrobe-preview.ts`): borrowed on open, handed
 * back with nothing fitted on close. It never writes `gameStateStore`.
 *
 * ─── Motion ─────────────────────────────────────────────────────────────────
 * The 18 animations of spec section 7 are CSS on transform and opacity, plus
 * two short JS-driven moments (the wallet count and the one-shot effects),
 * keyed off `AtelierState.event`. Twelve particle nodes at most, all pooled
 * here and never created per event. Everything pauses with the panel (a
 * display:none window runs no CSS animation, and the JS ticks are one-shot).
 * Under prefers-reduced-motion every state change is instant, a still frame
 * replaces each loop, and only dragging the survivor stays live.
 */
import { gameStateStore } from '../../state/game-state';
import type { EquipmentSlot, EquippedItemSummary } from '../../state/game-state.types';
import {
  atelierStore, buyAtelierItem, buyAtelierSet, clearFit, clearFitSlot, fitDelta, fitOverride,
  fitPiece, fitSet, loadAtelier, resetAtelierState, setAtelierTab, setShowBefore,
} from '../../state/atelier-state';
import {
  ATELIER_TABS, type AtelierBundle, type AtelierEntry, type AtelierState, type AtelierTab, type FitPiece,
} from '../../state/atelier-state.types';
import { EQUIPMENT_MODEL_BY_KEY, GARMENT_KEYS } from '../render/character-animations';
import { wardrobePreview } from './wardrobe-preview';
import { pictureHtml } from './paperdoll';
import { goldAmount } from './window-chrome';
import { formatStatMod, formatStatMods } from './stat-format';

const ART = '/atelier';

/** 100 Gold = 1 EUR at the base rate (spec 2). Shown in a tooltip only, never on the card. */
export function eurHint(gold: number): string {
  return `About ${(gold / 100).toLocaleString('en', { style: 'currency', currency: 'EUR' })} at the base rate`;
}

const TAB_LABEL: Record<AtelierTab, string> = {
  HATS: 'Hats', JACKETS: 'Jackets', VESTS: 'Vests', TROUSERS: 'Trousers', GLOVES: 'Gloves', BOOTS: 'Boots', SETS: 'Sets',
};
const TAB_ICON: Record<AtelierTab, string> = {
  HATS: 'hat', JACKETS: 'jacket', VESTS: 'vest', TROUSERS: 'trousers', GLOVES: 'gloves', BOOTS: 'boots', SETS: 'mannequin',
};
/** The six pins under the stage, in body order. */
const PIN_SLOTS: EquipmentSlot[] = ['HEADGEAR', 'TORSO', 'VEST', 'LEGS', 'GLOVES', 'FEET'];
const PIN_LABEL: Record<string, string> = {
  HEADGEAR: 'Head', TORSO: 'Jacket', VEST: 'Vest', LEGS: 'Trousers', GLOVES: 'Gloves', FEET: 'Boots',
};

const SPEECH = {
  idle: 'Couture from what’s left. Dress for the day after.',
  fit: 'Hold still, dear.',
  sold: 'Fine choice.',
  set: 'The whole lot. Fine choice.',
  short: 'Not on credit.',
  sewing: 'Hollis is still sewing this one.',
} as const;

/** Card frame material follows rarity: paper, brass, gilt (spec 5, principle 13: the object, not a chip). */
export function tierOf(rarity: string): 'paper' | 'brass' | 'gilt' {
  return rarity === 'RARE' ? 'gilt' : rarity === 'UNCOMMON' ? 'brass' : 'paper';
}

/** Can the stage show this piece? Garments need a skinned GLB; headgear a prop. */
export function hasFitModel(itemKey: string): boolean {
  return GARMENT_KEYS.includes(itemKey) || itemKey in EQUIPMENT_MODEL_BY_KEY;
}

export function reducedMotion(): boolean {
  return typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
}

// ─── Lifetime ───────────────────────────────────────────────────────────────

let root: HTMLElement | null = null;
let shopCol: HTMLElement | null = null;
/**
 * The scrolling column (2026-10-19, Janeks: "make the full shop scrollable,
 * only the top tabs stay frozen"): the Atelier heading and wallet now scroll
 * with the cards, so it wraps the head slot and `shopCol`, which is rewritten.
 */
let scrollCol: HTMLElement | null = null;
let headSlot: HTMLElement | null = null;
let stageUi: HTMLElement | null = null;
let previewHost: HTMLElement | null = null;
let speechEl: HTMLElement | null = null;
let fxLayer: HTMLElement | null = null;
let unsub: (() => void) | null = null;
let lastEventSeq = 0;
let lastTab: AtelierTab | null = null;
let active = false;
let speechTimer: ReturnType<typeof setTimeout> | null = null;
/** For the wallet count: what the counter last showed. */
let shownGold: number | null = null;
let lastColHtml = '';
let lastStageHtml = '';

/**
 * Builds the persistent root (once) and returns it for the shop to place in
 * its body. Event listeners are delegated on the root, so the rail's repaints
 * never re-bind them.
 */
export function atelierRoot(): HTMLElement {
  if (root) return root;
  root = document.createElement('div');
  root.className = 'bv-atl';
  root.innerHTML = `
    <section class="bv-atl-stage" aria-label="Fitting stage">
      <button type="button" class="bv-atl-grip" data-atl-grip aria-expanded="false">
        <span>Fitting stage</span>
      </button>
      <div class="bv-atl-stage-art" aria-hidden="true"></div>
      <div class="bv-atl-preview" data-atl-preview><span class="bv-atl-hint">drag to turn</span></div>
      <div class="bv-atl-flash" aria-hidden="true"></div>
      <div class="bv-atl-wipe" aria-hidden="true"></div>
      <!-- Hollis's portrait and speech bubble were removed 2026-10-19 (Janeks:
           "in the way of the ui and takes up space"). His lines still reach
           screen readers through this visually hidden status. -->
      <p class="bv-atl-status" data-atl-speech role="status"></p>
      <div class="bv-atl-stage-ui" data-atl-stage-ui></div>
    </section>
    <section class="bv-atl-shop" data-atl-scroll>
      <div class="bv-atl-head-slot" data-atl-head></div>
      <div data-atl-shop></div>
    </section>
    <div class="bv-atl-fx" data-atl-fx aria-hidden="true">
      ${Array.from({ length: 5 }, (_, i) => `<i class="bv-atl-mote" style="--i:${i}"></i>`).join('')}
      ${Array.from({ length: 3 }, (_, i) => `<i class="bv-atl-spark" style="--i:${i}"></i>`).join('')}
      <i class="bv-atl-puff"></i>
    </div>`;
  shopCol = root.querySelector('[data-atl-shop]');
  scrollCol = root.querySelector('[data-atl-scroll]');
  headSlot = root.querySelector('[data-atl-head]');
  stageUi = root.querySelector('[data-atl-stage-ui]');
  previewHost = root.querySelector('[data-atl-preview]');
  speechEl = root.querySelector('[data-atl-speech]');
  fxLayer = root.querySelector('[data-atl-fx]');
  root.addEventListener('click', onClick);
  scrollCol?.addEventListener('scroll', onScroll, { passive: true });
  return root;
}

/** The shop switched to the Atelier: borrow the preview, load, start listening. */
export function activateAtelier(): void {
  const el = atelierRoot();
  if (active) return;
  active = true;
  el.classList.remove('bv-atl-paused');
  // Entry: the bell and the pattern paper (#1), once per visit. On the WINDOW,
  // not the header: the header is rebuilt on every repaint and would replay it.
  const win = el.closest('.bv-win');
  if (win && !reducedMotion()) {
    win.classList.add('bv-atl-entering');
    setTimeout(() => win.classList.remove('bv-atl-entering'), 500);
  }
  const preview = wardrobePreview();
  if (previewHost) preview.mount(previewHost);
  preview.setActive(true);
  unsub = atelierStore.subscribe(onState);
  if (!atelierStore.get().catalog) void loadAtelier();
  onState(atelierStore.get());
}

/** Back to the Market, or the shop closed: hand the preview back with nothing fitted. */
export function deactivateAtelier(): void {
  if (!active) return;
  active = false;
  root?.classList.add('bv-atl-paused');
  lastColHtml = '';
  lastStageHtml = '';
  unsub?.();
  unsub = null;
  const preview = wardrobePreview();
  preview.setFit(null);
  preview.setActive(false);
  if (speechTimer) clearTimeout(speechTimer);
  speechTimer = null;
}

/** The whole shop closed: also forget the fitting. */
export function closeAtelier(): void {
  deactivateAtelier();
  resetAtelierState();
  lastTab = null;
  lastEventSeq = 0;
  shownGold = null;
}

export function disposeAtelier(): void {
  closeAtelier();
  root?.removeEventListener('click', onClick);
  scrollCol?.removeEventListener('scroll', onScroll);
  root?.remove();
  root = shopCol = scrollCol = headSlot = stageUi = previewHost = speechEl = fxLayer = null;
}

// ─── Rendering ──────────────────────────────────────────────────────────────

/** Repaint from outside (the shop calls this when Gold or equipment change). */
export function refreshAtelier(): void {
  onState(atelierStore.get());
}

function onState(state: AtelierState): void {
  if (!active || !shopCol || !stageUi) return;
  const gold = currentGold();
  const tabChanged = lastTab !== null && lastTab !== state.tab;
  // Rewritten only when the markup actually changed. The shop repaints on
  // every game-state push, and replacing a card under the pointer restarts its
  // hover sway and price swing (the same defect shop-ui.ts's patchClocks
  // documents for the Market's once-a-second render).
  const col = shopColumnHtml(state, gold);
  if (col !== lastColHtml) {
    shopCol.innerHTML = col;
    lastColHtml = col;
    shopCol.classList.toggle('bv-atl-tab-changed', tabChanged);
    if (tabChanged && scrollCol) scrollCol.scrollTop = 0;
  }
  lastTab = state.tab;
  const ui = stageUiHtml(state, worn());
  if (ui !== lastStageHtml) {
    stageUi.innerHTML = ui;
    lastStageHtml = ui;
  }
  wardrobePreview().setFit(fitOverride(state));
  if (state.event && state.event.seq !== lastEventSeq) {
    lastEventSeq = state.event.seq;
    playEvent(state.event.kind, state.event.key);
  }
  onScroll();
}

function currentGold(): number {
  const c = gameStateStore.get().character;
  return c?.state === 'ACTIVE' ? c.premiumBalance : 0;
}

function worn(): Partial<Record<string, EquippedItemSummary>> | undefined {
  return gameStateStore.get().equipment?.slots as Partial<Record<string, EquippedItemSummary>> | undefined;
}

export function shopColumnHtml(state: AtelierState, gold: number): string {
  const golden = state.bundles?.find((b) => b.key === 'set_golden');
  const parts: string[] = [];
  if (golden) parts.push(heroHtml(golden, gold, state.busy));
  parts.push(railHtml(state.tab));
  if (state.error) parts.push(`<p class="bv-win-err">${esc(state.error)}</p>`);
  if (state.receipt) parts.push(`<p class="bv-win-ok">${esc(state.receipt)}</p>`);
  if (!state.catalog) {
    parts.push(`<p class="bv-win-empty">${state.busy ? 'Hollis is unrolling the patterns…' : 'The rail is empty today.'}</p>`);
  } else if (state.tab === 'SETS') {
    parts.push(`<div class="bv-atl-grid bv-atl-grid-sets">${(state.bundles ?? []).map((b) => setCardHtml(b, gold, state.busy)).join('')}</div>`);
  } else {
    const cards = state.catalog.filter((e) => e.category === state.tab)
      .sort((a, b) => a.goldPrice - b.goldPrice || a.name.localeCompare(b.name));
    parts.push(`<div class="bv-atl-grid">${cards.map((e, i) => cardHtml(e, gold, state.busy, i)).join('')}</div>`);
  }
  parts.push('<div class="bv-atl-ruler" aria-hidden="true"><i data-atl-ruler></i></div>');
  return parts.join('');
}

function railHtml(current: AtelierTab): string {
  return `<nav class="bv-atl-rail" role="tablist" aria-label="Garments">${ATELIER_TABS.map((t, i) => {
    const on = t === current;
    return `<button type="button" role="tab" aria-selected="${on}" class="bv-atl-tab${on ? ' bv-atl-tab-on' : ''}"
      data-atl-tab="${t}" style="--i:${i}">
      <span class="bv-atl-tab-icon" style="--icon:url(${ART}/icons/icon-${TAB_ICON[t]}.svg)" aria-hidden="true"></span>
      <span class="bv-atl-tab-label">${TAB_LABEL[t]}</span></button>`;
  }).join('')}</nav>`;
}

/** The one-line perk text: armour, then each mod in player words. */
export function perkLine(stats: AtelierEntry['stats']): string {
  const bits: string[] = [];
  if (stats.armorPoints) bits.push(`${stats.armorPoints} armour`);
  for (const m of formatStatMods(stats.statMods)) bits.push(`${m.value} ${m.label.toLowerCase()}`);
  return bits.join(' · ') || 'Looks only';
}

export function cardHtml(e: AtelierEntry, gold: number, busy: boolean, i = 0): string {
  const short = !e.owned && gold < e.goldPrice;
  const tier = tierOf(e.rarity);
  const art = pictureHtml(e.iconPath, e.name, 'bv-atl-card-art', '120px')
    || '<span class="bv-atl-card-noart" aria-hidden="true">?</span>';
  const buy = e.owned
    ? '<button type="button" class="bv-atl-btn bv-atl-buy" disabled title="Already in your wardrobe">Owned</button>'
    : `<button type="button" class="bv-atl-btn bv-atl-buy" data-atl-buy="${esc(e.key)}"${busy ? ' disabled' : ''}
        ${short ? `data-short="1" title="${e.goldPrice - gold} Gold short"` : ''}>Buy</button>`;
  return `<article class="bv-atl-card bv-atl-tier-${tier}${e.owned ? ' bv-atl-owned' : ''}" data-atl-card="${esc(e.key)}" style="--i:${i}">
    <span class="bv-atl-pin" aria-hidden="true"></span>
    <div class="bv-atl-card-top">${art}</div>
    <div class="bv-atl-tag">
      <h3 class="bv-atl-name">${esc(e.name)}</h3>
      <p class="bv-atl-perk">${esc(perkLine(e.stats))}</p>
    </div>
    <div class="bv-atl-price" title="${esc(eurHint(e.goldPrice))}">${goldAmount(e.goldPrice.toLocaleString('en'))}</div>
    ${short ? '<button type="button" class="bv-atl-short" data-atl-topup>Top up</button>' : ''}
    <div class="bv-atl-acts">
      <button type="button" class="bv-atl-btn bv-atl-fit" data-atl-fit="${esc(e.key)}">Fit</button>
      ${buy}
    </div>
    <span class="bv-atl-tape" aria-hidden="true"></span>
    <span class="bv-atl-tissue" aria-hidden="true"></span>
    ${e.owned ? `<img class="bv-atl-stamp" src="${ART}/stamps/stamp-wardrobe.svg" alt="In your wardrobe" />` : ''}
  </article>`;
}

function bonusLine(b: AtelierBundle): string {
  const mods = Object.entries(b.bonus).map(([k, v]) => formatStatMod(k, v)).map((m) => `${m.value} ${m.label.toLowerCase()}`);
  return mods.length ? `Full set: ${mods.join(', ')}` : '';
}

export function setCardHtml(b: AtelierBundle, gold: number, busy: boolean): string {
  const all = b.piecesOwned >= b.pieces.length;
  const short = !all && gold < b.goldPrice;
  const fan = b.pieces.map((p, i) => `<span class="bv-atl-fan-piece" style="--i:${i};--n:${b.pieces.length}">${
    pictureHtml(p.iconPath, p.name, 'bv-atl-fan-art', '56px') || ''}</span>`).join('');
  const owned = b.piecesOwned > 0 && !all
    ? `<p class="bv-atl-owned-note">You already own ${b.piecesOwned} of these. The set price stays ${b.goldPrice.toLocaleString('en')}; you get the other ${b.pieces.length - b.piecesOwned}.</p>`
    : '';
  const buy = all
    ? '<button type="button" class="bv-atl-btn bv-atl-buy" disabled>Owned</button>'
    : `<button type="button" class="bv-atl-btn bv-atl-buy" data-atl-buyset="${esc(b.key)}"${busy ? ' disabled' : ''}
        ${short ? `data-short="1" title="${b.goldPrice - gold} Gold short"` : ''}>Buy set</button>`;
  return `<article class="bv-atl-card bv-atl-set${b.key === 'set_golden' ? ' bv-atl-tier-gold' : ' bv-atl-tier-brass'}${all ? ' bv-atl-owned' : ''}" data-atl-card="${esc(b.key)}">
    <span class="bv-atl-pin" aria-hidden="true"></span>
    <div class="bv-atl-fan">${fan}</div>
    <div class="bv-atl-tag">
      <h3 class="bv-atl-name">${esc(b.name)}</h3>
      <p class="bv-atl-perk">${esc(bonusLine(b))}</p>
      ${owned}
    </div>
    <div class="bv-atl-price" title="${esc(eurHint(b.goldPrice))}">
      ${b.singlesSum ? `<s class="bv-atl-was"><span class="bv-sr">Singly </span>${b.singlesSum.toLocaleString('en')}</s>` : '<span class="bv-atl-only">Set only</span>'}
      ${goldAmount(b.goldPrice.toLocaleString('en'))}
    </div>
    ${short ? '<button type="button" class="bv-atl-short" data-atl-topup>Top up</button>' : ''}
    <div class="bv-atl-acts">
      <button type="button" class="bv-atl-btn bv-atl-fit" data-atl-fitset="${esc(b.key)}">Try full set</button>
      ${buy}
    </div>
    <span class="bv-atl-ribbon" aria-hidden="true"></span>
    ${all ? `<img class="bv-atl-stamp" src="${ART}/stamps/stamp-owned.svg" alt="Owned" />` : ''}
  </article>`;
}

function heroHtml(b: AtelierBundle, gold: number, busy: boolean): string {
  const fan = b.pieces.map((p, i) => `<span class="bv-atl-fan-piece" style="--i:${i};--n:${b.pieces.length}">${
    pictureHtml(p.iconPath, p.name, 'bv-atl-fan-art', '64px') || ''}</span>`).join('');
  const all = b.piecesOwned >= b.pieces.length;
  return `<section class="bv-atl-hero" aria-label="The Golden Set">
    <div class="bv-atl-hero-foil" aria-hidden="true"></div>
    ${Array.from({ length: 6 }, (_, i) => `<i class="bv-atl-bulb" style="--i:${i}" aria-hidden="true"></i>`).join('')}
    ${Array.from({ length: 3 }, (_, i) => `<i class="bv-atl-hero-mote" style="--i:${i}" aria-hidden="true"></i>`).join('')}
    <div class="bv-atl-fan bv-atl-hero-fan">${fan}</div>
    <div class="bv-atl-hero-text">
      <h3 class="bv-atl-hero-title">The Golden Set</h3>
      <p class="bv-atl-hero-sub">Brocade, gilt and old medals. Six pieces, sold only together.</p>
      <p class="bv-atl-hero-price" title="${esc(eurHint(b.goldPrice))}">${goldAmount(b.goldPrice.toLocaleString('en'))}</p>
      <div class="bv-atl-acts">
        <button type="button" class="bv-atl-btn bv-atl-fit" data-atl-fitset="${esc(b.key)}">Try it on</button>
        ${all ? '<button type="button" class="bv-atl-btn bv-atl-buy" disabled>Owned</button>'
    : `<button type="button" class="bv-atl-btn bv-atl-buy" data-atl-buyset="${esc(b.key)}"${busy ? ' disabled' : ''}${gold < b.goldPrice ? ' data-short="1"' : ''}>Buy</button>`}
      </div>
    </div>
  </section>`;
}

export function stageUiHtml(state: AtelierState, wornSlots: Partial<Record<string, EquippedItemSummary>> | undefined): string {
  const pins = PIN_SLOTS.map((slot) => {
    const p = state.fit[slot];
    return p
      ? `<button type="button" class="bv-atl-slotpin bv-atl-slotpin-on" data-atl-unpin="${slot}" aria-label="Take off the ${esc(p.name)}">
          ${pictureHtml(p.iconPath, p.name, 'bv-atl-slotpin-art', '40px') || ''}<span>${PIN_LABEL[slot]}</span></button>`
      : `<span class="bv-atl-slotpin" aria-label="${PIN_LABEL[slot]}: as worn"><span>${PIN_LABEL[slot]}</span></span>`;
  }).join('');
  const any = Object.keys(state.fit).length > 0;
  const delta = any ? deltaHtml(fitDelta(wornSlots, state.fit, state.bundles)) : '';
  return `<div class="bv-atl-pins">${pins}</div>
    ${any ? `<div class="bv-atl-ba" role="group" aria-label="Compare">
      <button type="button" class="bv-atl-ba-btn" data-atl-before="1" aria-pressed="${state.showBefore}">Before</button>
      <button type="button" class="bv-atl-ba-btn" data-atl-before="0" aria-pressed="${!state.showBefore}">After</button>
      <button type="button" class="bv-atl-ba-btn" data-atl-clear>Clear</button>
    </div>` : '<p class="bv-atl-stage-note">Press Fit on any piece. It costs nothing.</p>'}
    ${delta}`;
}

/** "▲ +6 armour · ▼ -5% run speed". The glyph and the sign carry the meaning; colour only repeats it. */
export function deltaHtml(d: { armor: number; flat: Record<string, number> }): string {
  const rows: string[] = [];
  if (d.armor) rows.push(row(d.armor > 0, `${d.armor > 0 ? '+' : ''}${d.armor} armour`));
  for (const [k, v] of Object.entries(d.flat)) {
    const m = formatStatMod(k, v);
    rows.push(row(m.good, `${m.value} ${m.label.toLowerCase()}`));
  }
  if (!rows.length) return '<p class="bv-atl-delta">No change to your stats. Looks only.</p>';
  return `<p class="bv-atl-delta"><span class="bv-sr">Against what you wear: </span>${rows.join(' ')}</p>`;
  function row(good: boolean, text: string): string {
    return `<span class="bv-atl-d ${good ? 'bv-atl-d-up' : 'bv-atl-d-down'}">${good ? '▲' : '▼'} ${esc(text)}<span class="bv-sr">${good ? ' (better)' : ' (worse)'}</span></span>`;
  }
}

// ─── Events ─────────────────────────────────────────────────────────────────

function pieceOf(e: AtelierEntry): FitPiece | null {
  const slot = e.stats.slot as EquipmentSlot | null;
  return slot ? { itemKey: e.itemKey, name: e.name, slot, iconPath: e.iconPath, stats: e.stats } : null;
}

function onClick(ev: Event): void {
  const t = ev.target as HTMLElement | null;
  if (!t) return;
  const state = atelierStore.get();
  const hit = (attr: string) => t.closest<HTMLElement>(`[${attr}]`);

  const tab = hit('data-atl-tab');
  if (tab) { setAtelierTab(tab.dataset.atlTab as AtelierTab); return; }

  const fit = hit('data-atl-fit');
  if (fit) {
    const e = state.catalog?.find((c) => c.key === fit.dataset.atlFit);
    const piece = e && pieceOf(e);
    if (piece) {
      tapeOn(fit.closest('[data-atl-card]'));
      fitPiece(piece, hasFitModel(piece.itemKey));
    }
    return;
  }
  const fitset = hit('data-atl-fitset');
  if (fitset) {
    const b = state.bundles?.find((x) => x.key === fitset.dataset.atlFitset);
    if (b) {
      const pieces = b.pieces.filter((p) => p.slot && hasFitModel(p.itemKey))
        .map((p) => ({ itemKey: p.itemKey, name: p.name, slot: p.slot!, iconPath: p.iconPath, stats: p.stats }));
      tapeOn(fitset.closest('[data-atl-card]'));
      fitSet(pieces, b.key);
      if (state.tab !== 'SETS' && fitset.closest('.bv-atl-hero')) setAtelierTab('SETS');
    }
    return;
  }
  const buy = hit('data-atl-buy');
  if (buy) {
    if (buy.dataset.short) { shake(buy.closest('[data-atl-card]')); say('short'); return; }
    void buyAtelierItem(buy.dataset.atlBuy!);
    return;
  }
  const buyset = hit('data-atl-buyset');
  if (buyset) {
    if (buyset.dataset.short) { shake(buyset.closest('[data-atl-card]') ?? buyset.closest('.bv-atl-hero')); say('short'); return; }
    void buyAtelierSet(buyset.dataset.atlBuyset!);
    return;
  }
  const unpin = hit('data-atl-unpin');
  if (unpin) {
    const slot = unpin.dataset.atlUnpin as EquipmentSlot;
    // The pin flicks off (#9), then the slot clears.
    if (reducedMotion()) clearFitSlot(slot);
    else { unpin.classList.add('bv-atl-slotpin-off'); setTimeout(() => clearFitSlot(slot), 150); }
    return;
  }
  const before = hit('data-atl-before');
  if (before) {
    const show = before.dataset.atlBefore === '1';
    if (show !== state.showBefore) { arm(root?.querySelector('.bv-atl-wipe') ?? null, 'bv-atl-wiping'); setShowBefore(show); }
    return;
  }
  if (hit('data-atl-clear')) { clearFit(); return; }
  if (hit('data-atl-topup')) {
    void import('./currency-shop-ui').then((m) => m.openCurrencyShopUI());
    return;
  }
  const grip = hit('data-atl-grip');
  if (grip) {
    const open = root?.classList.toggle('bv-atl-sheet-open') ?? false;
    grip.setAttribute('aria-expanded', String(open));
  }
}

/** The tape-measure ruler under the rail tracks how far the grid has scrolled (#18). */
function onScroll(): void {
  const bar = shopCol?.querySelector<HTMLElement>('[data-atl-ruler]');
  if (!bar || !scrollCol) return;
  const max = scrollCol.scrollHeight - scrollCol.clientHeight;
  bar.style.transform = `scaleX(${max > 0 ? Math.min(1, scrollCol.scrollTop / max) : 1})`;
}

function playEvent(kind: string, key: string): void {
  const card = root?.querySelector<HTMLElement>(`[data-atl-card="${CSS.escape(key)}"]`) ?? null;
  switch (kind) {
    case 'fit':
      arm(root?.querySelector('.bv-atl-preview') ?? null, 'bv-atl-pop');
      arm(root?.querySelector('.bv-atl-flash') ?? null, 'bv-atl-flashing');
      say('fit');
      break;
    case 'sewing':
      say('sewing');
      break;
    case 'sold':
      arm(card, 'bv-atl-selling');
      puffAt(card);
      sparks();
      say('sold');
      break;
    case 'set':
      arm(card, 'bv-atl-boxing');
      sparks();
      say('set');
      break;
    case 'short':
      shake(card);
      say('short');
      break;
    default:
  }
}

/** Restart a one-shot CSS animation by re-adding its class. */
function arm(el: Element | null, cls: string): void {
  if (!el || reducedMotion()) return;
  el.classList.remove(cls);
  void (el as HTMLElement).offsetWidth;
  el.classList.add(cls);
}

function tapeOn(card: Element | null): void { arm(card, 'bv-atl-taping'); }

function shake(card: Element | null): void {
  if (!card) return;
  // Reduced motion still gets a cue: one outline pulse instead of the shake (#13).
  const cls = reducedMotion() ? 'bv-atl-short-pulse' : 'bv-atl-shaking';
  card.classList.remove(cls);
  void (card as HTMLElement).offsetWidth;
  card.classList.add(cls);
}

/** Hollis's line for screen readers (the visible portrait and bubble are gone). Clears after 4 s. */
function say(kind: keyof typeof SPEECH): void {
  if (!speechEl) return;
  speechEl.textContent = SPEECH[kind];
  if (speechTimer) clearTimeout(speechTimer);
  speechTimer = setTimeout(() => {
    if (speechEl) speechEl.textContent = '';
  }, 4000);
}

/** The one pooled dust puff, moved under the stamp as it lands (#11). Transform only. */
function puffAt(card: Element | null): void {
  const puff = fxLayer?.querySelector<HTMLElement>('.bv-atl-puff');
  if (!puff || !card || !fxLayer || reducedMotion()) return;
  const c = card.getBoundingClientRect();
  const f = fxLayer.getBoundingClientRect();
  puff.style.transform = `translate(${Math.round(c.left - f.left + c.width / 2 - 40)}px, ${Math.round(c.top - f.top + c.height * 0.4)}px)`;
  arm(puff, 'bv-atl-puffing');
}

/** Three pooled ingot sparks fly to the wallet (#12). */
function sparks(): void {
  if (reducedMotion() || !fxLayer) return;
  for (const s of fxLayer.querySelectorAll<HTMLElement>('.bv-atl-spark')) arm(s, 'bv-atl-sparking');
}

/**
 * The wallet line, drawn by the shop's header. It shows what was last shown;
 * `animateWallet` then counts it to the real balance over 500ms (#12), so a
 * purchase visibly ticks down however late the balance arrives (it comes back
 * over the socket). Under reduced motion it snaps.
 */
/** Where the shop writes the Atelier heading and wallet: the top of the scrolling column. */
export function atelierHeadSlot(): HTMLElement | null {
  return headSlot;
}

export function atelierWalletHtml(gold: number): string {
  const shown = shownGold ?? gold;
  return `<p class="bv-shop-wallet bv-atl-wallet">
    <span data-atl-gold>${goldAmount(shown.toLocaleString('en'))}</span>
    <button type="button" class="bv-atl-topup" data-atl-topup>Top up</button>
  </p>`;
}

export function animateWallet(to: number): void {
  const from = shownGold ?? to;
  shownGold = to;
  const el = document.querySelector<HTMLElement>('[data-atl-gold]');
  if (!el || from === to) return;
  if (reducedMotion()) { el.innerHTML = goldAmount(to.toLocaleString('en')); return; }
  const start = performance.now();
  const step = (now: number) => {
    const k = Math.min(1, (now - start) / 500);
    const v = Math.round(from + (to - from) * (1 - (1 - k) ** 3));
    if (el.isConnected) el.innerHTML = goldAmount(v.toLocaleString('en'));
    if (k < 1 && active && el.isConnected) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  })[c]!);
}
